import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Printer, Download } from "lucide-react";
import WolfCrest from "@/components/WolfCrest";
import { trackView } from "@/lib/api";
import { PETITIONS } from "@/data/petitions";

export default function PetitionPage() {
  const { slug } = useParams();
  const petition = PETITIONS.find((p) => p.slug === slug);

  useEffect(() => {
    if (!petition) return;
    const key = `petition:${slug}`;
    const seen = JSON.parse(sessionStorage.getItem("views_tracked") || "[]");
    if (!seen.includes(key)) {
      sessionStorage.setItem("views_tracked", JSON.stringify([...seen, key]));
      trackView(key).catch(() => {});
    }
  }, [slug, petition]);

  if (!petition) return <Navigate to="/" replace />;

  return (
    <main className="min-h-screen bg-[#F8F9FA]" data-testid="petition-page">
      <header className="bg-navy text-[#F8F9FA] px-5 md:px-10 py-4 flex items-center justify-between print:hidden">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-gold transition-colors"
          data-testid="petition-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="flex items-center gap-3">
          <WolfCrest className="w-7 h-7" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/70">Referendum Solutions</span>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-electric mb-4">
          Petition {petition.n} · For Referendum Vote to the Three Affiliated Tribes
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-navy mb-8" data-testid="petition-title">
          {petition.title}
        </h1>

        <p className="text-navy/70 text-base md:text-lg leading-relaxed italic border-l-2 border-gold pl-5 mb-10">
          {petition.preamble}
        </p>

        <h2 className="text-xs uppercase tracking-[0.3em] font-bold text-navy mb-8 border-b border-navy/15 pb-3">
          Proposed Referendum
        </h2>

        <div className="space-y-7">
          {petition.clauses.map((c, i) => (
            <div key={i} className="flex gap-5 items-start" data-testid={`petition-clause-${i + 1}`}>
              <span className="font-serif text-2xl font-black text-electric/70 leading-none mt-0.5 w-8 shrink-0 text-right">
                {i + 1}.
              </span>
              <p className="text-navy/85 text-base md:text-lg leading-relaxed">{c}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-navy/15 flex flex-col sm:flex-row gap-4 print:hidden">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-700 text-white text-xs uppercase tracking-[0.2em] font-bold px-6 py-3.5 rounded-full transition-colors"
            data-testid="petition-print-btn"
          >
            <Printer className="w-4 h-4" /> Print / Save PDF
          </button>
          <a
            href={petition.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 text-navy hover:text-electric border border-navy/30 hover:border-electric/60 text-xs uppercase tracking-[0.2em] font-bold px-6 py-3.5 rounded-full transition-colors"
            data-testid="petition-download-btn"
          >
            <Download className="w-4 h-4" /> Download original
          </a>
        </div>
        <p className="text-xs text-steel italic mt-6">
          The official document includes signature sheets for enrolled members. Print this page or download
          the original to sign and circulate.
        </p>
      </article>
    </main>
  );
}
